import { useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SettingsPanel } from './tabs/SettingsPanel';
import { LayoutGrid, MessageSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@strivelabs/ui-core/v2';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarProvider,
  type LinkRenderer,
  type NavSection,
  useSidebar,
} from '@strivelabs/ui-blocks/v2';
import { useAppStore } from '@/store/appStore';
import { useUserRole } from '@/hooks/useUserRole';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';
import { SidebarBrand } from './header/SidebarBrand';
import { CollapseToggle } from './header/CollapseToggle';
import { OrgSwitcher } from './header/OrgSwitcher';
import { UserMenu } from './footer/UserMenu';
import { WorkspaceTab } from './tabs/WorkspaceTab';
import { ChatTab } from './tabs/chat/ChatTab';
import { useNewChatShortcut } from './hooks/useNewChat';
import type { SidebarPanel } from './types';

/**
 * Everything between header and footer.
 *
 * The collapsed branch is the only place that knows about the rail: at 52px
 * there is no room for a tab strip or a chat list, so both are dropped rather
 * than squeezed.
 *
 * Guests take the same branch for a different reason: chat is not among the
 * routes they may visit, so a Chat tab would offer them a panel whose every
 * row redirects. Their nav is a single Reports entry, which needs no tab strip.
 */
function SidebarTabs({ sections }: { sections: NavSection[] }) {
  const { collapsed, resizing } = useSidebar();
  const { isGuest, isLoaded } = useUserRole();
  const panel = useAppStore(state => state.sidebarPanel);
  const setSidebarPanel = useAppStore(state => state.setSidebarPanel);
  const { pathname } = useLocation();

  // Under /ui/settings the middle region becomes the settings nav, in place of
  // the Workspace/Chat tabs. Derived from the URL, so entering or leaving
  // settings needs no extra state and the previous panel is restored on return.
  if (pathname.startsWith('/ui/settings')) {
    return (
      <SidebarContent>
        <SettingsPanel />
      </SidebarContent>
    );
  }

  // Until the role resolves, `isGuest` is false, so rendering now would flash a
  // guest the full tab strip and nav before it collapses to their single entry.
  // Skeleton until we know — the account row and org switcher do the same.
  if (!isLoaded) {
    return (
      <SidebarContent>
        <div aria-hidden className="flex flex-col gap-1.5 px-1 py-1">
          {[0, 1, 2, 3, 4].map(row => (
            <div key={row} className="h-8 animate-pulse rounded-v2-md bg-v2-sidebar-accent motion-reduce:animate-none" />
          ))}
        </div>
      </SidebarContent>
    );
  }

  if (collapsed || isGuest) {
    return (
      <SidebarContent>
        <WorkspaceTab sections={sections} />
      </SidebarContent>
    );
  }

  return (
    <Tabs
      value={panel}
      onValueChange={value => setSidebarPanel(value as SidebarPanel)}
      variant="segment"
      // Freeze the indicator mid-drag: it re-projects each frame as the panel
      // width changes, and springing every one of those reads as lag.
      transition={resizing ? { duration: 0 } : undefined}
      className="flex min-h-0 flex-1 flex-col px-2"
    >
      <TabsList className="w-full bg-v2-sidebar-accent">
        <TabsTrigger
          value="workspace"
          className="flex-1 gap-1.5 text-v2-sm focus-visible:ring-offset-v2-sidebar-accent"
        >
          <LayoutGrid size={14} aria-hidden />
          Workspace
        </TabsTrigger>
        <TabsTrigger
          value="chat"
          className="flex-1 gap-1.5 text-v2-sm focus-visible:ring-offset-v2-sidebar-accent"
        >
          <MessageSquare size={14} aria-hidden />
          Chat
        </TabsTrigger>
      </TabsList>

      <div aria-hidden className="mt-2 h-px bg-v2-sidebar-border" />
      <SidebarContent className="mt-1 px-0">
        <TabsContent value="workspace" className="mt-0">
          <WorkspaceTab sections={sections} />
        </TabsContent>
        <TabsContent value="chat" className="mt-0 h-full">
          <ChatTab />
        </TabsContent>
      </SidebarContent>
    </Tabs>
  );
}

/**
 * Logo and collapse control.
 *
 * Expanded, both are visible side by side. In the rail there is only room for
 * one, so the logo holds the slot and gives way to the toggle on hover — the
 * mark identifies the product at rest, and the control appears when the pointer
 * says you are reaching for it. Both stay mounted and swap opacity rather than
 * conditionally rendering, so the toggle is still reachable by keyboard;
 * `focus-within` reveals it for that case.
 */
function SidebarMasthead() {
  const { collapsed } = useSidebar();

  if (!collapsed) {
    return (
      <div className="flex items-center justify-between gap-2">
        <SidebarBrand />
        <CollapseToggle />
      </div>
    );
  }

  return (
    <div className="relative grid h-9 place-items-center">
      <span className="col-start-1 row-start-1 transition-opacity duration-150 group-hover/sidebar:opacity-0 group-focus-within/sidebar:opacity-0 motion-reduce:transition-none">
        <SidebarBrand />
      </span>
      <span className="pointer-events-none col-start-1 row-start-1 opacity-0 transition-opacity duration-150 group-hover/sidebar:pointer-events-auto group-hover/sidebar:opacity-100 group-focus-within/sidebar:pointer-events-auto group-focus-within/sidebar:opacity-100 motion-reduce:transition-none">
        <CollapseToggle />
      </span>
    </div>
  );
}

/**
 * Reports sidebar collapse/expand. Watches the provider's state rather than
 * wrapping {@link CollapseToggle}'s click, because ⌘B is bound inside the
 * ui-core Sidebar shell — tracking only the button would miss every keyboard
 * use. Renders nothing; it just needs to be inside the provider to read it.
 */
function CollapseTracker() {
  const { collapsed } = useSidebar();
  const trackEvent = useTrack();
  const previousRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Skip the first run: the initial (possibly persisted) state is not a
    // change the user just made.
    if (previousRef.current === null) {
      previousRef.current = collapsed;
      return;
    }
    if (previousRef.current === collapsed) return;
    previousRef.current = collapsed;
    trackEvent(ANALYTICS_EVENTS.SIDEBAR_TOGGLED, { collapsed });
  }, [collapsed, trackEvent]);

  return null;
}

/** The application sidebar. Assembles the shell; all logic lives below it. */
export function AppSidebar({ sections }: { sections: NavSection[] }) {
  const { pathname } = useLocation();
  const { isGuest, isLoaded } = useUserRole();
  useNewChatShortcut({ enabled: isLoaded && !isGuest });

  const renderLink: LinkRenderer = useCallback(
    ({ href, className, children, onClick, ...rest }) => (
      <Link to={href} className={className} onClick={onClick} {...rest}>
        {children}
      </Link>
    ),
    [],
  );

  // On the home lobby the sidebar sits on the ambient blob wash (painted by
  // AppShell behind the whole viewport): its own ground goes transparent so
  // the wash reads through, and `sidebar-on-wash` steps the resting label ink
  // up (see index.css) so it stays legible on the tint. Every other route
  // keeps the opaque grey.
  const isHomeLobby = pathname === '/ui';

  return (
    <SidebarProvider sections={sections} currentPath={pathname} renderLink={renderLink}>
      <CollapseTracker />
      <Sidebar label="Main" className={isHomeLobby ? 'sidebar-on-wash bg-transparent' : undefined}>
        <SidebarHeader className="p-0">
          {/* The brand bar matches the page header height (h-[52px]) so its
              bottom border lines up with the header's separator across the
              sidebar edge. It also separates the product mark from the
              workspace it applies to. */}
          <div className="flex h-[52px] flex-col justify-center border-b border-v2-sidebar-border px-2">
            <SidebarMasthead />
          </div>
          <div className="flex flex-col px-2 pb-2">
            <OrgSwitcher />
          </div>
        </SidebarHeader>

        <SidebarTabs sections={sections} />

        <SidebarFooter>
          <UserMenu />
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}
